/**
 * @file CabinetBackdrop.tsx
 * @description Фон кабинета — «шёлковые ленты» (phase19.31, SC65, SC66).
 *
 * Рисунок — готовая маска из тончайших нитей (`assets/brand/cabinet-silk-*.webp`,
 * генератор `scripts/generate-cabinet-silk.mjs`): белые нити на прозрачном
 * фоне. Цвет даёт градиент темы под маской — тёплый тон → конец → начало
 * градиента палитры (`--gradient-warm` / `--gradient-end` / `--gradient-start`).
 * Так рисунок общий для всех шаблонов, а цвета — из палитры (SC3), hex здесь нет.
 *
 * Две маски: широкая (2000×1250) — с 768px, вертикальная (800×1600) — на
 * телефоне. Обе прижаты к правому нижнему углу (`mask-position`) и
 * заполняют слой с обрезкой (`mask-size: cover`) — без искажений.
 * Адреса масок — CSS-переменные слоя (`--cabinet-silk-wide/-tall`): файл
 * подключает сборка Vite, правила и плотность — раздел 11
 * `styles/console-rules.css`. Без правил кабинета у слоя нет фона — не виден.
 *
 * Слой вставляет `AppShell.Content` первым ребёнком `<main>`: закреплён
 * относительно окна (не двигается при прокрутке), поверх фона страницы и под
 * содержимым; шапка, меню, шторки и окна — выше. `aria-hidden`, клики не
 * перехватывает, при печати скрыт, без анимации.
 *
 * Фона нет (признаки данными, а не списком адресов):
 *  - страница на всю ширину (`useFluidLayout`: доски заказов/возвратов и
 *    сделок, редактор страницы сайта) — проп `off` от каркаса;
 *  - экран кладовщика — признак `isWarehouseMode`;
 *  - вход и служебные страницы — они вне каркаса.
 */

import { useContext } from "react";
import type { CSSProperties } from "react";

import silkTallUrl from "../../assets/brand/cabinet-silk-tall.webp";
import silkWideUrl from "../../assets/brand/cabinet-silk-wide.webp";
import { AuthContext } from "../../auth/AuthContext";
import { isWarehouseMode } from "../../lib/shop-nav";

/**
 * Геометрия слоя (от края боковой панели до правого края, от шапки до низа
 * окна) и адреса масок для правил раздела 11.
 */
const LAYER_STYLE = {
  left: "var(--app-bar-left, 0px)",
  top: "calc(var(--shell-top, 0px) + var(--shell-header-h, 4rem))",
  "--cabinet-silk-wide": `url("${silkWideUrl}")`,
  "--cabinet-silk-tall": `url("${silkTallUrl}")`,
} as CSSProperties;

interface CabinetBackdropProps {
  /** Страница на всю ширину (доски, редактор) — фона нет. */
  off?: boolean;
}

export function CabinetBackdrop({ off = false }: CabinetBackdropProps) {
  // Контекст входа необязателен: каркас рисуют и в тестах без провайдера.
  const auth = useContext(AuthContext);
  const warehouse = isWarehouseMode(auth?.me?.activeContext ?? null);
  if (off || warehouse) return null;

  return (
    <div
      aria-hidden
      data-slot="cabinet-backdrop"
      className="cabinet-backdrop pointer-events-none fixed right-0 bottom-0 print:hidden"
      style={LAYER_STYLE}
    />
  );
}
